import { AccountHomeKeys } from 'events/lib/accountHome'
import { useEvent } from 'events/useEvent'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

type HelpfulResourcesLinkItem = {
  text: string
  url: string
  elementId: Extract<
    AccountHomeKeys,
    | 'accounthome_usingyourmedicarecov_click'
    | 'accounthome_medicarecosts_click'
    | 'accounthome_claimsappeals_click'
    | 'accounthome_reportmedicarefraud_click'
  >
}
function useHelpfulResourceLinks (): HelpfulResourcesLinkItem[] {
  const { t, i18n } = useTranslation('accountHome', {
    keyPrefix: 'helpfulResources.links'
  })

  return useMemo(() => {
    const links = [
      {
        text: t('usingCoverage'),
        urls: {
          en: 'https://www.medicare.gov/basics/get-started-with-medicare/using-medicare',
          es: 'https://es.medicare.gov/basics/get-started-with-medicare/using-medicare'
        },
        elementId:
          'accounthome_usingyourmedicarecov_click' as HelpfulResourcesLinkItem['elementId']
      },
      {
        text: t('medicareCosts'),
        urls: {
          en: 'https://www.medicare.gov/basics/costs',
          es: 'https://es.medicare.gov/basics/costs'
        },
        elementId:
          'accounthome_medicarecosts_click' as HelpfulResourcesLinkItem['elementId']
      },
      {
        text: t('claimsAppeals'),
        urls: {
          en: 'https://www.medicare.gov/claims-appeals',
          es: 'https://es.medicare.gov/claims-appeals'
        },
        elementId:
          'accounthome_claimsappeals_click' as HelpfulResourcesLinkItem['elementId']
      },
      {
        text: t('reportingFraud'),
        urls: {
          en: 'https://www.medicare.gov/basics/reporting-medicare-fraud-and-abuse',
          es: 'https://es.medicare.gov/basics/reporting-medicare-fraud-and-abuse'
        },
        elementId:
          'accounthome_reportmedicarefraud_click' as HelpfulResourcesLinkItem['elementId']
      }
    ]

    return links.map(({ text, urls, elementId }) => ({
      text,
      url: urls[i18n.language as 'en' | 'es'],
      elementId
    }))
  }, [i18n.language, t])
}

type HelpfulResourcesProps = {
  className?: string
}
export const HelpfulResources = ({ className }: HelpfulResourcesProps) => {
  const { t } = useTranslation('accountHome')
  const { fireEvent } = useEvent('accountHome')

  const links = useHelpfulResourceLinks()

  function eventHandler (item: HelpfulResourcesLinkItem) {
    fireEvent(item.elementId, {
      linkText: item.text,
      linkUrl: item.url
    })
  }

  return (
    <div className={`${className}`}>
      <h2
        className="ds-text-heading--xl ds-u-margin-bottom--2 ds-u-lg-margin-bottom--3"
        id="helpful-resources-heading"
      >
        {t('helpfulResources.heading')}
      </h2>
      <nav aria-labelledby="helpful-resources-heading">
        <ul className="ds-c-list ds-c-list--bare">
          {links.map((item, idx) => (
            <li key={idx}>
              <a href={item.url} onClick={() => eventHandler(item)}>
                {item.text}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  )
}
